import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { supabase } from '../../supabaseClient'


const DIMS = [
  { key: 'difficulty',        label: 'Difficulty'        },
  { key: 'novelty',           label: 'Novelty'           },
  { key: 'data_availability', label: 'Data Availability' },
  { key: 'feasibility',       label: 'Feasibility'       },
]

function ratingColor(r: string) {
  if (r === 'high')   return 'error'
  if (r === 'medium') return 'warning'
  return 'success'
}

interface DemoTopic {
  id:                  string
  title:               string
  description:         string
  spanning_categories: string[] | null
}

interface Props {
  projectId: string
  studentId: string
}

export default function DemoTopicsTab({ projectId, studentId }: Props) {
  const [topics,     setTopics]     = useState<DemoTopic[]>([])
  const [comparison, setComparison] = useState<any | null>(null)
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        const { data, error: invokeErr } = await supabase.functions.invoke('generate-demo-topics', {
          body: { project_id: projectId, student_id: studentId },
        })
        if (invokeErr || data?.error) throw new Error(invokeErr?.message ?? data?.error ?? 'Failed to generate preview topics')
        setTopics(data.topics ?? [])
        setComparison(data.comparison ?? null)
      } catch (e: any) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [projectId, studentId])

  if (loading) return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <CircularProgress size={28} sx={{ mb: 1 }} />
      <Typography variant="body2" color="text.secondary">Generating your preview topics…</Typography>
    </Box>
  )

  if (error) return <Alert severity="error">{error}</Alert>

  const comparisons: any[] = comparison?.comparisons ?? []
  const recommendation: string = comparison?.recommendation ?? ''

  return (
    <Box sx={{ maxWidth: 780, py: 2 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Research Topic Preview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Based on your research categories, here are three topics you could explore.
            This is a sample of what your full research program looks like.
          </Typography>
        </Box>
        <Chip
          icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
          label="Preview"
          size="small"
          sx={{ bgcolor: 'secondary.main', color: '#fff', fontWeight: 600, flexShrink: 0 }}
        />
      </Box>

      {/* Comparison table */}
      {comparisons.length > 0 && (
        <>
          <Box sx={{ overflowX: 'auto', mb: 3 }}>
            <Table size="small" sx={{ minWidth: 480, tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '26%' }} />
                <col style={{ width: '26%' }} />
                <col style={{ width: '26%' }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Dimension</TableCell>
                  {comparisons.map((c: any, i: number) => (
                    <TableCell key={i} align="center" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                      {topics[i]?.title ?? `Topic ${i + 1}`}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {DIMS.map(({ key, label }) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.8rem' }}>
                      {label}
                    </TableCell>
                    {comparisons.map((c: any, i: number) => {
                      const d = c[key]
                      return (
                        <TableCell key={i} align="center">
                          {d ? (
                            <Chip
                              label={d.rating}
                              size="small"
                              color={ratingColor(d.rating) as any}
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          ) : '—'}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {recommendation && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>AI suggestion:</strong> {recommendation}
            </Alert>
          )}
        </>
      )}

      {/* Topic detail cards — read only */}
      {topics.map((topic, i) => {
        const comp = comparisons[i]
        return (
          <Card key={topic.id} variant="outlined" sx={{ mb: 2, opacity: 0.9 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {(topic.spanning_categories ?? []).map(c => (
                  <Chip key={c} label={c} size="small" variant="outlined" color="primary" />
                ))}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{topic.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{topic.description}</Typography>
              {comp && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {DIMS.map(({ key, label }) => comp[key] && (
                    <Box key={key}>
                      <Typography variant="caption" color="text.secondary">{label}: </Typography>
                      <Chip
                        label={comp[key].rating}
                        size="small"
                        color={ratingColor(comp[key].rating) as any}
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        )
      })}

      {/* Promo card */}
      <Card
        sx={{
          mt: 4,
          bgcolor:    'primary.main',
          color:      '#fff',
          borderRadius: 2,
          border:     'none',
          overflow:   'hidden',
          position:   'relative',
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute', right: -60, top: -60,
            width: 240, height: 240, borderRadius: '50%',
            border: '40px solid rgba(255,255,255,0.05)',
            pointerEvents: 'none',
          }}
        />
        <CardContent sx={{ p: 4 }}>
          <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: '0.1em' }}>
            Ready to go further?
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', mt: 0.5, mb: 1.5, maxWidth: 460 }}>
            Turn this preview into your actual research project.
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, maxWidth: 480, lineHeight: 1.7 }}>
            In the full program, you'll work with an expert mentor to refine your topic, design your study,
            collect real data, and compete. Everything you've done here carries over.
          </Typography>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            href="https://ardentresearch.org"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              bgcolor:     'secondary.main',
              color:       '#fff',
              fontWeight:  700,
              px:          3,
              py:          1.25,
              '&:hover':   { bgcolor: 'secondary.dark' },
            }}
          >
            Apply to Ardent Research
          </Button>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', mt: 2 }}>
            Enrollment is by application. Your mentor will be in touch.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}
